import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Modal, Form, Button, Row, Col, Alert, Spinner, Tab, Tabs } from 'react-bootstrap';
import { ThemeContext } from '../context/ThemeContext';
import { useAlert } from '../context/AlertContext';
import magService from '../services/magService';
import ProductImage from './ProductImage';
import useIsMobile from '../hooks/useIsMobile';



// Componente modale per eliminazione categoria
const EliminaCategoriaModal = ({ show, onHide, onConfirm, categoriaData, darkMode, eliminando }) => {
  const { nomeCategoria, numeroGruppo, codiceCategoria } = categoriaData || {};

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      centered
      className={darkMode ? 'modal-dark' : ''}
      data-bs-theme={darkMode ? 'dark' : 'light'}
    >
      <Modal.Header closeButton className={darkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Modal.Title>
          <i className="fas fa-trash me-2 text-danger"></i>
          Elimina Categoria
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={darkMode ? 'bg-dark text-light' : ''}>
        <p className="mb-3">
          Sei sicuro di voler eliminare la categoria <strong>"{nomeCategoria}"</strong> (Codice: {codiceCategoria})?
        </p>
        <p className="text-muted small mb-0">
          <i className="fas fa-exclamation-triangle me-1"></i>
          Questa operazione non può essere annullata.
        </p>
      </Modal.Body>
      <Modal.Footer className={darkMode ? 'bg-dark border-secondary' : ''}>
        <Button 
          variant="outline-secondary" 
          onClick={onHide}
          disabled={eliminando}
        >
          Annulla
        </Button>
        <Button 
          variant="danger" 
          onClick={onConfirm}
          disabled={eliminando}
        >
          {eliminando ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Eliminazione...
            </>
          ) : (
            <>
              <i className="fas fa-trash me-2"></i>
              Elimina
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};



// Componente modale per creazione nuova categoria
const NuovaCategoriaModal = ({ show, onHide, onSave, numeroGruppo, etichettaGruppo, darkMode }) => {
  const [nomeCategoria, setNomeCategoria] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!nomeCategoria.trim()) {
      return;
    }

    setSaving(true);
    try {
      await onSave(numeroGruppo, nomeCategoria.trim());
      setNomeCategoria('');
      onHide();
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setNomeCategoria('');
    onHide();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !saving && nomeCategoria.trim()) {
      handleSave();
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose}
      size="lg"
      centered
      className={darkMode ? 'modal-dark' : ''}
      data-bs-theme={darkMode ? 'dark' : 'light'}
    >
      <Modal.Header closeButton className={darkMode ? 'bg-dark text-light border-secondary' : ''}>
        <Modal.Title>
          <i className="fas fa-plus me-2 text-success"></i>
          Nuova {etichettaGruppo}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={darkMode ? 'bg-dark text-light' : ''}>
        <Form>
          <Form.Group>
            <Form.Label>
              <strong>Nome {etichettaGruppo}:</strong>
            </Form.Label>
            <Form.Control
              type="text"
              value={nomeCategoria}
              onChange={(e) => setNomeCategoria(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Inserisci il nome della ${etichettaGruppo.toLowerCase()}...`}
              autoFocus
              disabled={saving}
              className={darkMode ? 'bg-dark text-light border-secondary' : ''}
            />
          </Form.Group>
          
        </Form>
      </Modal.Body>
      <Modal.Footer className={darkMode ? 'bg-dark border-secondary' : ''}>
        <Button 
          variant="success" 
          onClick={handleSave}
          disabled={!nomeCategoria.trim() || saving}
        >
          {saving ? (
            <>
              <Spinner size="sm" className="me-1" />
              Salvataggio...
            </>
          ) : (
            <>
              <i className="fas fa-save me-1"></i>
              Salva
            </>
          )}
        </Button>
        <Button 
          variant="secondary" 
          onClick={handleClose}
          disabled={saving}
        >
          <i className="fas fa-times me-1"></i>
          Annulla
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Stili per modalità mobile
const mobileStyles = `
  .hide-tab-nav .nav-tabs { display: none !important; }
  
  @media (max-width: 991.98px) {
    .modal-fullscreen .modal-header {
      position: sticky !important;
      top: 0;
      z-index: 1051;
      border-radius: 0;
    }
    
    .modal-fullscreen .modal-footer {
      position: fixed !important;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 1051;
      margin: 0;
      border-radius: 0;
      width: 100%;
    }
    
    .modal-fullscreen .modal-body {
      padding-bottom: 80px !important;
      overflow-y: auto;
    }
    
    .nav-tabs-mobile .nav-link {
      font-size: 0.875rem;
      padding: 0.5rem 0.75rem;
      min-height: 44px;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
    }
    
    .nav-tabs-mobile {
      flex-wrap: nowrap;
      overflow-x: auto;
      border-bottom: 1px solid #dee2e6;
    }
    
    .nav-tabs-mobile .nav-item {
      flex-shrink: 0;
    }
    
    .form-label {
      font-size: 0.875rem;
      margin-bottom: 0.25rem;
      font-weight: 600;
    }
    
    .form-control, .form-select {
      font-size: 0.875rem;
      padding: 0.5rem 0.75rem;
      min-height: 44px;
    }
    
    .btn {
      padding: 0.5rem 1rem;
      font-size: 0.875rem;
      min-height: 44px;
    }
    
    .btn-sm {
      padding: 0.375rem 0.75rem;
      font-size: 0.75rem;
      min-height: 36px;
    }
    
    .row {
      margin-left: -0.5rem;
      margin-right: -0.5rem;
    }
    
    .row > * {
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }
    
    .mb-3 {
      margin-bottom: 1rem !important;
    }
    
    .modal-footer .btn {
      min-height: 48px;
      font-weight: 600;
    }
    
    .modal-footer {
      padding: 1rem;
    }
    
    /* Stili per i pulsanti categoria su mobile */
    .categoria-buttons .btn-sm {
      min-width: 36px;
      width: 36px;
      height: 36px;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  
  /* Stili generali per i pulsanti categoria */
  .categoria-buttons {
    display: flex;
    gap: 0.25rem;
    flex-shrink: 0;
  }
  
  .categoria-buttons .btn-sm {
    min-width: 32px;
    width: 32px;
    height: 32px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.375rem;
  }
  
  .categoria-buttons .btn-outline-danger {
    border-color: #dc3545;
    color: #dc3545;
  }
  
  .categoria-buttons .btn-outline-danger:hover {
    background-color: #dc3545;
    border-color: #dc3545;
    color: #fff;
  }
  
  .categoria-buttons .btn-outline-success {
    border-color: #198754;
    color: #198754;
  }
  
  .categoria-buttons .btn-outline-success:hover {
    background-color: #198754;
    border-color: #198754;
    color: #fff;
  }
  
  .categoria-combo-container {
    display: flex;
    align-items: end;
    gap: 0.5rem;
  }
  
  .categoria-combo-container .flex-grow-1 {
    flex: 1;
  }
`;

const EditArticolo = ({ show, onHide, articoloDettagli, isNewArticolo, onArticoloUpdate, hidePrezziMagazzinoTabs = false }) => {
  const { darkMode } = useContext(ThemeContext);
  const { showAlertGlobal } = useAlert();
  
  // Inietta gli stili mobile
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = mobileStyles;
    document.head.appendChild(styleSheet);
    
    return () => {
      if (document.head.contains(styleSheet)) {
        document.head.removeChild(styleSheet);
      }
    };
  }, []);
  
  // Stati del form - mappati sui campi della response di getArticolo
  const [formData, setFormData] = useState({
    // Campi principali obbligatori
    cd_min: '',
    descriz: '',
    iva: '',
    unita_m: '',
    
    // Campi identificativi
    cod_for: '0001',  // Valore di default fisso
    barcode: '',
    codice_alt: '',
    barcode_clie: '',
    cod_bancale: '',
    cod_eti: '',
    
    // Prezzi e costi
    cost_medio: '',
    costo_u: '',
    costo_futu: '',
    costo_lordo: '',
    costo_lavo: '',
    listino1: '',
    listino2: '',
    list_saldi: '',
    
    // Sconti
    sconto1: '',
    sconto2: '',
    
    // Quantità e giacenze
    giacenza: '',
    impegnato: '',
    ordinato: '',
    scort_m: '',
    qtamin: '',
    qtaminima: '',
    qtarange: '',
    orebloccoprod: '',
    
    // Dimensioni e pesi
    peso_lo: '',
    volumes: '',
    confezione: '',
    altezza: '',
    larghezza: '',
    spessore: '',
    area: '',
    pm: '',
    pos: '',
    
    // Categorie e gruppi (collegati ai combo)
    colore: '',      // gruppo1_mag.codice_g1
    tipo_arti: '',   // gruppo2_mag.codice_g2
    ngruppo3: '',    // gruppo3_mag
    ncolore: '',     // gruppo4_mag
    gruppo5: '',     // gruppo5_mag
    gruppo6: '',     // gruppo6_mag
    mgruppo7: '',
    
    // Posizione e logistica
    posizione: '',
    pag_cat: '',
    
    // Date
    data_u_scr: '',
    data_aq: '',
    data_inizzio: '',
    data_fine: '',
    scade_abb: '',
    data_foto: '',
    data_tecnica: '',
    data_sicurezza: '',
    
    // Contabilità
    contro: '',
    contoa: '',
    controc: '',
    prov: '',
    
    // Caratteristiche tecniche
    tono: '',
    calibro: '',
    mq_scat: '',
    mq_pal: '',
    gomma: '',
    euromt: '',
    tipo_cod: '',
    macchina: '',
    cod_dogana: '',
    
    // Note e descrizioni
    note_est: '',
    descriz_lang: '',
    scheda_tec: '',
    scheda_sic: '',
    
    // References
    figliopa: '',
    clie_rif: '',
    padre: '',
    serie: '',
    
    // Immagini
    foto: '',
    newfoto: '',
    newfoto2: '',
    urlimmagine: '',
    
    // Flag booleani (0/1)
    ordinamento: '0',
    arti_o_lavo: '1',
    inv: '0',
    duplicab: '0',
    cal_inv: '0',
    no_scontrino: '0',
    tracciabi: '1',
    ricetta: '0',
    saldi: '0',
    su_web: '1',  // PanifyCloud attivo di default
    su_flash: '0',
    stampuni: '0',
    art_a_peso: '0',
    no_scade: '0',
    packorari: '0',
    front_da_stampare: '0',
    lavo1: '0',
    sche_tec: '0',
    c_aee: '0',
    tipotes: '0',
    sottocomp_finiti: '0',
    d1: '0'
  });

  // Stati separati per i dati dinamici dei combo
  const [gruppo1Data, setGruppo1Data] = useState([]);    // Categorie
  const [gruppo2Data, setGruppo2Data] = useState([]);    // Tipologie
  const [gruppo3Data, setGruppo3Data] = useState([]);
  const [gruppo4Data, setGruppo4Data] = useState([]);
  const [gruppo5Data, setGruppo5Data] = useState([]);
  const [gruppo6Data, setGruppo6Data] = useState([]);
  const [unitaMisura, setUnitaMisura] = useState([]);
  const [aliquoteIva, setAliquoteIva] = useState([]);
  
  // Stati per le etichette dinamiche dei gruppi
  const [gruppo1Label, setGruppo1Label] = useState('Categoria');
  const [gruppo2Label, setGruppo2Label] = useState('Tipologia');
  const [gruppo3Label, setGruppo3Label] = useState('Gruppo 3');
  const [gruppo4Label, setGruppo4Label] = useState('Gruppo 4');
  const [gruppo5Label, setGruppo5Label] = useState('Gruppo 5');
  const [gruppo6Label, setGruppo6Label] = useState('Gruppo 6');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Stati per il modale nuova categoria
  const [showNuovaCategoriaModal, setShowNuovaCategoriaModal] = useState(false);
  const [categoriaModalData, setCategoriaModalData] = useState({
    numeroGruppo: 1,
    etichettaGruppo: 'Categoria'
  });
  
  // Stati per il modale conferma eliminazione categoria
  const [showEliminaCategoriaModal, setShowEliminaCategoriaModal] = useState(false);
  const [eliminaCategoriaData, setEliminaCategoriaData] = useState({
    numeroGruppo: 1,
    codiceCategoria: '',
    nomeCategoria: '',
    etichettaGruppo: '',
    fieldName: ''
  });
  const [eliminandoCategoria, setEliminandoCategoria] = useState(false);
  
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const isMobile = useIsMobile();

  // Carica tutti i dati per i combo con una singola chiamata
  const loadComboData = useCallback(async (forceRefresh = false) => {
    try {
      setLoading(true);
      
      console.log('🔄 === INIZIO CARICAMENTO COMBO DATA ===');
      console.log('🔄 Caricamento dati combo con singola chiamata...', forceRefresh ? '(FORCE REFRESH)' : '');
      console.log('🌐 API Base URL:', process.env.REACT_APP_API_BASE_URL || 'default');
      
      // Una singola chiamata che restituisce tutti i dati
      // Se forceRefresh è true, forza il refresh della cache
      console.log('📡 Chiamando magService.getCategorieCompleteData...');
      const categorieComplete = await magService.getCategorieCompleteData(forceRefresh);
      
      console.log('✅ === RISPOSTA API CATEGORIE ===');
      console.log('✅ Dati combo caricati:', categorieComplete);
      console.log('📊 Struttura gruppi:', Object.keys(categorieComplete.gruppi || {}));
      console.log('📋 Gruppo1 data length:', categorieComplete.gruppi?.gruppo1?.data?.length || 0);
      console.log('📋 Gruppo2 data length:', categorieComplete.gruppi?.gruppo2?.data?.length || 0);
      
      // Estrae i dati dai gruppi già frammentati
      setGruppo1Data(categorieComplete.gruppi.gruppo1?.data || []);
      setGruppo2Data(categorieComplete.gruppi.gruppo2?.data || []);
      setGruppo3Data(categorieComplete.gruppi.gruppo3?.data || []);
      setGruppo4Data(categorieComplete.gruppi.gruppo4?.data || []);
      setGruppo5Data(categorieComplete.gruppi.gruppo5?.data || []);
      setGruppo6Data(categorieComplete.gruppi.gruppo6?.data || []);
      
      // Applica trim alle unità di misura per sicurezza
      const unitaMisuraTrimmed = (categorieComplete.unitaMisura || []).map(unita => {
        if (typeof unita === 'object' && unita.codice) {
          return {
            ...unita,
            codice: unita.codice.toString().trim(),
            nome: unita.nome ? unita.nome.toString().trim() : unita.nome
          };
        } else if (typeof unita === 'string') {
          return unita.trim();
        }
        return unita;
      });
      
      // Applica trim alle aliquote IVA per sicurezza
      const aliquoteIvaTrimmed = (categorieComplete.aliquoteIva || []).map(aliquota => {
        if (typeof aliquota === 'object' && aliquota.codice) {
          return {
            ...aliquota,
            codice: aliquota.codice.toString().trim(),
            nome: aliquota.nome ? aliquota.nome.toString().trim() : aliquota.nome
          };
        } else if (typeof aliquota === 'string') {
          return aliquota.trim();
        }
        return aliquota;
      });
      
      setUnitaMisura(unitaMisuraTrimmed);
      setAliquoteIva(aliquoteIvaTrimmed);
      
      // Estrae e salva le etichette dinamiche
      setGruppo1Label(categorieComplete.gruppi.gruppo1?.etichetta || 'Categoria');
      setGruppo2Label(categorieComplete.gruppi.gruppo2?.etichetta || 'Tipologia');
      setGruppo3Label(categorieComplete.gruppi.gruppo3?.etichetta || 'Gruppo 3');
      setGruppo4Label(categorieComplete.gruppi.gruppo4?.etichetta || 'Gruppo 4');
      setGruppo5Label(categorieComplete.gruppi.gruppo5?.etichetta || 'Gruppo 5');
      setGruppo6Label(categorieComplete.gruppi.gruppo6?.etichetta || 'Gruppo 6');
      
      console.log('🏷️ Etichette dinamiche impostate:', {
        gruppo1: categorieComplete.gruppi.gruppo1?.etichetta,
        gruppo2: categorieComplete.gruppi.gruppo2?.etichetta,
        gruppo3: categorieComplete.gruppi.gruppo3?.etichetta,
        gruppo4: categorieComplete.gruppi.gruppo4?.etichetta,
        gruppo5: categorieComplete.gruppi.gruppo5?.etichetta,
        gruppo6: categorieComplete.gruppi.gruppo6?.etichetta
      });
      
    } catch (error) {
      console.error('❌ === ERRORE CARICAMENTO COMBO DATA ===');
      console.error('❌ Errore nel caricamento dei dati combo:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      console.error('❌ Error stack:', error.stack);
      showAlertGlobal(`Errore nel caricamento dei dati: ${error.message}`, 'danger');
    } finally {
      setLoading(false);
    }
  }, [showAlertGlobal]);

  // Carica i dati completi di un articolo per la modifica
  const loadArticoloCompleto = useCallback(async (cd_min) => {
    try {
      console.log('🔍 Caricamento articolo completo:', cd_min);
      
      setLoading(true);
      
      const response = await magService.getArticolo(cd_min);
      console.log('✅ Dati articolo completo:', response);
      
      // La response di getArticolo ha la struttura: { data: { ... } }
      const articolo = response.data || response;
      
      console.log('📊 Articolo estratto:', articolo);
      
      // Mappa tutti i campi dalla response al form
      const mappedData = {
        // Campi principali con trim applicato
        cd_min: articolo.cd_min ? articolo.cd_min.toString().trim() : '',
        descriz: articolo.descriz ? articolo.descriz.toString().trim() : '',
        iva: articolo.iva ? articolo.iva.toString().trim() : '',
        unita_m: articolo.unita_m ? articolo.unita_m.toString().trim() : '',
        
        // Campi identificativi
        cod_for: articolo.cod_for || '',
        barcode: articolo.barcode || '',
        codice_alt: articolo.codice_alt || '',
        barcode_clie: articolo.barcode_clie || '',
        cod_bancale: articolo.cod_bancale || '',
        cod_eti: articolo.cod_eti || '',
        
        // Prezzi e costi
        cost_medio: articolo.cost_medio || '',
        costo_u: articolo.costo_u || '',
        costo_futu: articolo.costo_futu || '',
        costo_lordo: articolo.costo_lordo || '',
        costo_lavo: articolo.costo_lavo || '',
        listino1: articolo.listino1 || '',
        listino2: articolo.listino2 || '',
        list_saldi: articolo.list_saldi || '',
        
        // Sconti
        sconto1: articolo.sconto1 || '',
        sconto2: articolo.sconto2 || '',
        
        // Quantità e giacenze
        giacenza: articolo.giacenza || '',
        impegnato: articolo.impegnato || '',
        ordinato: articolo.ordinato || '',
        scort_m: articolo.scort_m || '',
        qtamin: articolo.qtamin || '',
        qtaminima: articolo.qtaminima || '',
        qtarange: articolo.qtarange || '',
        orebloccoprod: (() => {
          const raw = articolo.orebloccoprod ?? articolo.dati_articolo?.orebloccoprod;
          if (raw === undefined || raw === null || String(raw).trim() === '') return '';
          const n = Math.max(0, parseInt(String(raw).replace(/\D/g, ''), 10) || 0);
          return String(n);
        })(),
        
        // Dimensioni e pesi
        peso_lo: articolo.peso_lo || '',
        volumes: articolo.volumes || '',
        confezione: articolo.confezione || '',
        altezza: articolo.altezza || '',
        larghezza: articolo.larghezza || '',
        spessore: articolo.spessore || '',
        area: articolo.area || '',
        pm: articolo.pm || '',
        pos: articolo.pos || '',
        
        // Categorie e gruppi - mappati dai gruppi nella response con trim
        colore: articolo.gruppo1_mag?.codice_g1 ? articolo.gruppo1_mag.codice_g1.toString().trim() : (articolo.colore ? articolo.colore.toString().trim() : ''),
        tipo_arti: articolo.gruppo2_mag?.codice_g2 ? articolo.gruppo2_mag.codice_g2.toString().trim() : (articolo.tipo_arti ? articolo.tipo_arti.toString().trim() : ''),
        ngruppo3: articolo.gruppo3_mag?.codice_g3 ? articolo.gruppo3_mag.codice_g3.toString().trim() : (articolo.ngruppo3 ? articolo.ngruppo3.toString().trim() : ''),
        ncolore: articolo.gruppo4_mag?.codice_g4 ? articolo.gruppo4_mag.codice_g4.toString().trim() : (articolo.ncolore ? articolo.ncolore.toString().trim() : ''),
        gruppo5: articolo.gruppo5_mag?.codice_g5 ? articolo.gruppo5_mag.codice_g5.toString().trim() : (articolo.gruppo5 ? articolo.gruppo5.toString().trim() : ''),
        gruppo6: articolo.gruppo6_mag?.codice_g6 ? articolo.gruppo6_mag.codice_g6.toString().trim() : (articolo.gruppo6 ? articolo.gruppo6.toString().trim() : ''),
        mgruppo7: articolo.mgruppo7 || '',
        
        // Posizione e logistica
        posizione: articolo.posizione || '',
        pag_cat: articolo.pag_cat || '',
        
        // Date
        data_u_scr: articolo.data_u_scr || '',
        data_aq: articolo.data_aq || '',
        data_inizzio: articolo.data_inizzio || '',
        data_fine: articolo.data_fine || '',
        scade_abb: articolo.scade_abb || '',
        data_foto: articolo.data_foto || '',
        data_tecnica: articolo.data_tecnica || '',
        data_sicurezza: articolo.data_sicurezza || '',
        
        // Contabilità
        contro: articolo.contro || '',
        contoa: articolo.contoa || '',
        controc: articolo.controc || '',
        prov: articolo.prov || '',
        
        // Caratteristiche tecniche
        tono: articolo.tono || '',
        calibro: articolo.calibro || '',
        mq_scat: articolo.mq_scat || '',
        mq_pal: articolo.mq_pal || '',
        gomma: articolo.gomma || '',
        euromt: articolo.euromt || '',
        tipo_cod: articolo.tipo_cod || '',
        macchina: articolo.macchina || '',
        cod_dogana: articolo.cod_dogana || '',
        
        // Note e descrizioni
        note_est: articolo.note_est || '',
        descriz_lang: articolo.descriz_lang || '',
        scheda_tec: articolo.scheda_tec || '',
        scheda_sic: articolo.scheda_sic || '',
        
        // References
        figliopa: articolo.figliopa || '',
        clie_rif: articolo.clie_rif || '',
        padre: articolo.padre || '',
        serie: articolo.serie || '',
        
        // Immagini
        foto: articolo.foto || '',
        newfoto: articolo.newfoto || '',
        newfoto2: articolo.newfoto2 || '',
        urlimmagine: articolo.urlimmagine || '',
        
        // Flag booleani
        ordinamento: articolo.ordinamento || '0',
        arti_o_lavo: articolo.arti_o_lavo || '1',
        inv: articolo.inv || '0',
        duplicab: articolo.duplicab || '0',
        cal_inv: articolo.cal_inv || '0',
        no_scontrino: articolo.no_scontrino || '0',
        tracciabi: articolo.tracciabi || '1',
        ricetta: articolo.ricetta || '0',
        saldi: articolo.saldi || '0',
        su_web: articolo.su_web || '0',
        su_flash: articolo.su_flash || '0',
        stampuni: articolo.stampuni || '0',
        art_a_peso: articolo.art_a_peso || '0',
        no_scade: articolo.no_scade || '0',
        packorari: articolo.packorari || '0',
        front_da_stampare: articolo.front_da_stampare || '0',
        lavo1: articolo.lavo1 || '0',
        sche_tec: articolo.sche_tec || '0',
        c_aee: articolo.c_aee || '0',
        tipotes: articolo.tipotes || '0',
        sottocomp_finiti: articolo.sottocomp_finiti || '0',
        d1: articolo.d1 || '0'
      };
      
      console.log('📊 Form data mappato:', mappedData);
      
      setFormData(mappedData);
      
    } catch (error) {
      console.error('❌ Errore nel caricamento articolo:', error);
      showAlertGlobal('Errore nel caricamento dell\'articolo', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showAlertGlobal]);

  // Reset form per nuovo articolo
  const resetForm = useCallback(() => {
    setFormData({
      // Campi principali vuoti
      cd_min: '',
      descriz: '',
      iva: '',
      unita_m: '',
      
      // Tutti gli altri campi vuoti
      cod_for: '0001',  // Valore di default fisso
      barcode: '',
      codice_alt: '',
      barcode_clie: '',
      cod_bancale: '',
      cod_eti: '',
      cost_medio: '',
      costo_u: '',
      costo_futu: '',
      costo_lordo: '',
      costo_lavo: '',
      listino1: '',
      listino2: '',
      list_saldi: '',
      sconto1: '',
      sconto2: '',
      giacenza: '',
      impegnato: '',
      ordinato: '',
      scort_m: '',
      qtamin: '',
      qtaminima: '',
      qtarange: '',
      orebloccoprod: '',
      peso_lo: '',
      volumes: '',
      confezione: '',
      altezza: '',
      larghezza: '',
      spessore: '',
      area: '',
      pm: '',
      pos: '',
      colore: '',
      tipo_arti: '',
      ngruppo3: '',
      ncolore: '',
      gruppo5: '',
      gruppo6: '',
      mgruppo7: '',
      posizione: '',
      pag_cat: '',
      data_u_scr: '',
      data_aq: '',
      data_inizzio: '',
      data_fine: '',
      scade_abb: '',
      data_foto: '',
      data_tecnica: '',
      data_sicurezza: '',
      contro: '',
      contoa: '',
      controc: '',
      prov: '',
      tono: '',
      calibro: '',
      mq_scat: '',
      mq_pal: '',
      gomma: '',
      euromt: '',
      tipo_cod: '',
      macchina: '',
      cod_dogana: '',
      note_est: '',
      descriz_lang: '',
      scheda_tec: '',
      scheda_sic: '',
      figliopa: '',
      clie_rif: '',
      padre: '',
      serie: '',
      foto: '',
      newfoto: '',
      newfoto2: '',
      urlimmagine: '',
      
      // Flag booleani con valori di default
      ordinamento: '0',
      arti_o_lavo: '1',
      inv: '0',
      duplicab: '0',
      cal_inv: '0',
      no_scontrino: '0',
      tracciabi: '1',
      ricetta: '0',
      saldi: '0',
      su_web: '1',  // PanifyCloud attivo di default per nuovo articolo
      su_flash: '0',
      stampuni: '0',
      art_a_peso: '0',
      no_scade: '0',
      packorari: '0',
      front_da_stampare: '0',
      lavo1: '0',
      sche_tec: '0',
      c_aee: '0',
      tipotes: '0',
      sottocomp_finiti: '0',
      d1: '0'
    });
  }, []);

  // Carica dati al mount del componente
  useEffect(() => {
    if (show) {
      loadComboData();
    }
  }, [show, loadComboData]);

  // Effetto per caricare/resettare dati articolo
  useEffect(() => {
    if (show && gruppo1Data.length > 0) {
      console.log('🔄 Gestione dati articolo:', {
        isNewArticolo,
        articoloDettagli,
        hasComboData: gruppo1Data.length > 0
      });
      
      if (isNewArticolo) {
        console.log('📝 Nuovo articolo - reset form');
        resetForm();
      } else if (articoloDettagli?.cd_min) {
        console.log('✏️ Modifica articolo - carico dati completi');
        loadArticoloCompleto(articoloDettagli.cd_min);
      }
    }
  }, [show, isNewArticolo, articoloDettagli?.cd_min, gruppo1Data.length, resetForm, loadArticoloCompleto]);

  // Gestione cambiamenti input
  const handleInputChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    if (name === 'cd_min') {
      // Consenti solo: A-Z, 0-9, punto, trattino, underscore
      value = value.toUpperCase().replace(/[^A-Z0-9._-]/g, '');
    }
    if (name === 'pos') {
      // Pz x Teglia: solo interi, nessun decimale
      value = value.replace(/\D/g, '');
    }
    if (name === 'orebloccoprod') {
      value = value.replace(/\D/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Rimuovi errore se esiste
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Gestione caricamento immagine
  const handleImageUpload = async (file) => {
    if (!file || !formData.cd_min) {
      showAlertGlobal('Inserire prima il codice articolo', 'warning');
      return;
    }

    try {
      setUploadingImage(true);
      
      // Converte il file in base64 come si aspetta il server
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const base64Data = e.target.result.split(',')[1]; // Rimuove il prefisso data:image/...;base64,
          const filename = `${formData.cd_min.toUpperCase()}.jpg`; // Usa codice in maiuscolo come nel sistema esistente

          // In produzione l'upload immagini passa dal proxy/app (non dall'API).
          // In locale "frontend-only" usiamo un origin configurabile.
          const webProxyOrigin =
            window.REACT_APP_WEB_PROXY_ORIGIN ||
            process.env.REACT_APP_WEB_PROXY_ORIGIN ||
            '';
          const uploadUrl = webProxyOrigin ? `${webProxyOrigin}/img/upload` : '/img/upload';
          
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              filename: filename,
              imageData: base64Data
            }),
            // Se l'endpoint usa cookie/sessione, mantieni i cookie
            credentials: 'include',
          });

          const contentType = response.headers.get('content-type') || '';
          const result = contentType.includes('application/json')
            ? await response.json()
            : { success: false, error: `Risposta non JSON (HTTP ${response.status})` };

          if (result.success) {
            showAlertGlobal('Immagine caricata con successo', 'success');
            
            // Aggiorna il campo foto nel form
            setFormData(prev => ({
              ...prev,
              foto: result.filename || filename
            }));
          } else {
            const msg = result.error || result.message || `Errore upload (HTTP ${response.status})`;
            console.error('❌ Upload immagine fallito:', { uploadUrl, status: response.status, result });
            throw new Error(msg);
          }
        } catch (error) {
          console.error('❌ Errore upload immagine:', error);
          showAlertGlobal(`Errore durante il caricamento dell'immagine: ${error.message}`, 'danger');
        } finally {
          setUploadingImage(false);
          setImageFile(null);
        }
      };
      
      reader.onerror = () => {
        showAlertGlobal('Errore nella lettura del file', 'danger');
        setUploadingImage(false);
        setImageFile(null);
      };
      
      reader.readAsDataURL(file);
      
    } catch (error) {
      console.error('❌ Errore upload immagine:', error);
      showAlertGlobal('Errore durante il caricamento dell\'immagine', 'danger');
      setUploadingImage(false);
      setImageFile(null);
    }
  };

  // Gestione selezione file immagine
  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Verifica che sia un'immagine
      if (!file.type.startsWith('image/')) {
        showAlertGlobal('Seleziona un file immagine valido', 'warning');
        return;
      }
      
      // Verifica dimensione (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showAlertGlobal('Il file deve essere inferiore a 5MB', 'warning');
        return;
      }
      
      setImageFile(file);
      handleImageUpload(file);
    }
  };

  // Calcolo dinamico Pz al Kg
  const calcolaPzAlKg = () => {
    const unitaMisura = (formData.unita_m || '').toLowerCase().trim();
    const peso = parseFloat(formData.peso_lo) || 0;
    
    if (peso === 0) return 0;
    
    // Unità di misura che rappresentano pezzi
    const unitaPezzi = ['pz', 'pezzi', 'n', 'nr', 'numero'];
    // Unità di misura che rappresentano peso
    const unitaPeso = ['kg', 'kilogrammi', 'kilo'];
    
    if (unitaPezzi.includes(unitaMisura)) {
      // Se l'unità è "pezzi", calcola quanti pezzi ci sono in 1 kg
      return (1 / peso).toFixed(3);
    } else if (unitaPeso.includes(unitaMisura)) {
      // Se l'unità è "kg", "peso_lo" rappresenta Kg per pezzo:
      // Pz al Kg = 1 / (Kg per Pz)
      return (1 / peso).toFixed(3);
    }
    
    // Per altre unità di misura, calcola come per i pezzi
    return peso > 0 ? (1 / peso).toFixed(3) : 0;
  };

  // Validazione form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.cd_min?.trim()) {
      newErrors.cd_min = 'Codice articolo è obbligatorio';
    } else if (!/^[A-Z0-9._-]+$/.test(formData.cd_min.trim())) {
      newErrors.cd_min = 'Usa solo maiuscole, numeri e i simboli . - _';
    }
    if (!formData.descriz?.trim()) {
      newErrors.descriz = 'Descrizione è obbligatoria';
    }
    if (!formData.iva?.trim()) {
      newErrors.iva = 'IVA è obbligatoria';
    }
    if (!formData.unita_m?.trim()) {
      newErrors.unita_m = 'Unità di misura è obbligatoria';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Salvataggio articolo
  const handleSave = async () => {
    console.log('🚀 === INIZIO SALVATAGGIO ARTICOLO ===');
    console.log('📋 Form data completo:', formData);
    console.log('🆔 Tipo operazione:', isNewArticolo ? 'NUOVO' : 'MODIFICA');
    
    if (!validateForm()) {
      console.log('❌ Validazione fallita');
      return;
    }
    
    try {
      setSaving(true);
      
      // Crea un oggetto con solo i campi editabili (esclusi quelli delle schede Prezzi e Magazzino)
      const campiEditabili = {
        // Campi principali obbligatori
        cd_min: formData.cd_min,
        descriz: formData.descriz,
        iva: formData.iva,
        unita_m: formData.unita_m,
        
        // Campi identificativi
        cod_for: formData.cod_for,
        barcode: formData.barcode,
        codice_alt: formData.codice_alt,
        barcode_clie: formData.barcode_clie,
        cod_bancale: formData.cod_bancale,
        cod_eti: formData.cod_eti,
        
        // Dimensioni e pesi (solo quelli editabili)
        peso_lo: formData.peso_lo,
        volumes: formData.volumes,
        confezione: formData.confezione,
        altezza: formData.altezza,
        larghezza: formData.larghezza,
        spessore: formData.spessore,
        area: formData.area,
        pm: formData.pm,
        pos: formData.pos,
        qtarange: formData.qtarange,
        qtaminima: formData.qtaminima,
        orebloccoprod: (() => {
          const s = String(formData.orebloccoprod ?? '').replace(/\D/g, '');
          if (s === '') return 0;
          const n = parseInt(s, 10);
          return Number.isFinite(n) && n >= 0 ? n : 0;
        })(),
        
        // Categorie e gruppi
        colore: formData.colore,
        tipo_arti: formData.tipo_arti,
        ngruppo3: formData.ngruppo3,
        ncolore: formData.ncolore,
        gruppo5: formData.gruppo5,
        gruppo6: formData.gruppo6,
        mgruppo7: formData.mgruppo7,
        
        // Note e descrizioni
        note_est: formData.note_est,
        descriz_lang: formData.descriz_lang,
        scheda_tec: formData.scheda_tec,
        scheda_sic: formData.scheda_sic,
        
        // References
        figliopa: formData.figliopa,
        clie_rif: formData.clie_rif,
        padre: formData.padre,
        serie: formData.serie,
        
        // Immagini
        foto: formData.foto,
        newfoto: formData.newfoto,
        newfoto2: formData.newfoto2,
        urlimmagine: formData.urlimmagine,
        
        // Flag booleani
        ordinamento: formData.ordinamento,
        arti_o_lavo: '1', // sempre 1 in insert/update
        inv: formData.inv,
        duplicab: formData.duplicab,
        cal_inv: formData.cal_inv,
        no_scontrino: formData.no_scontrino,
        tracciabi: formData.tracciabi,
        ricetta: formData.ricetta,
        saldi: formData.saldi,
        su_web: formData.su_web,
        su_flash: formData.su_flash,
        stampuni: formData.stampuni,
        art_a_peso: formData.art_a_peso,
        no_scade: formData.no_scade,
        packorari: formData.packorari,
        front_da_stampare: formData.front_da_stampare,
        lavo1: formData.lavo1,
        sche_tec: formData.sche_tec,
        c_aee: formData.c_aee,
        tipotes: formData.tipotes,
        sottocomp_finiti: formData.sottocomp_finiti,
        d1: formData.d1
        
        // ESCLUSI: tutti i campi delle schede Prezzi e Magazzino
        // (listino1, listino2, cost_medio, costo_u, costo_lordo, costo_lavo, 
        //  list_saldi, sconto1, sconto2, giacenza, impegnato, ordinato, 
        //  scort_m, posizione, qtamin, ecc.)
      };
      
      console.log('🧹 === PREPARAZIONE DATI ===');
      console.log('� Campi editabili preparati:', Object.keys(campiEditabili));
      console.log('📋 Valori dei campi:', campiEditabili);
      console.log('🔢 Numero campi da inviare:', Object.keys(campiEditabili).length);
      
      // Controllo presenza campi obbligatori
      const campiObbligatori = ['cd_min', 'descriz', 'iva', 'unita_m'];
      const campiMancanti = campiObbligatori.filter(campo => !campiEditabili[campo]);
      if (campiMancanti.length > 0) {
        console.error('❌ Campi obbligatori mancanti:', campiMancanti);
        throw new Error(`Campi obbligatori mancanti: ${campiMancanti.join(', ')}`);
      }
      
      console.log('✅ Tutti i campi obbligatori presenti');
      console.log('📡 === CHIAMATA API ===');
      
      if (isNewArticolo) {
        console.log('🆕 Chiamata insertMagazzino...');
        console.log('📤 Dati inviati:', campiEditabili);
        const result = await magService.insertMagazzino(campiEditabili);
        console.log('✅ Risposta insertMagazzino:', result);
        showAlertGlobal('Articolo creato con successo', 'success');
      } else {
        console.log('✏️ Chiamata updateMagazzino...');
        console.log('🔑 Codice articolo:', campiEditabili.cd_min);
        console.log('📤 Dati inviati:', campiEditabili);
        const result = await magService.updateMagazzino(campiEditabili.cd_min, campiEditabili);
        console.log('✅ Risposta updateMagazzino:', result);
        showAlertGlobal('Articolo aggiornato con successo', 'success');
      }
      
      console.log('🎉 === SALVATAGGIO COMPLETATO ===');
      onArticoloUpdate();
      onHide();
      
    } catch (error) {
      console.error('💥 === ERRORE DURANTE SALVATAGGIO ===');
      console.error('❌ Errore completo:', error);
      console.error('📜 Stack trace:', error.stack);
      console.error('🌐 Response error:', error.response?.data);
      console.error('📊 Status code:', error.response?.status);
      console.error('📋 Headers:', error.response?.headers);

      // Priorità: apiErrorMessage da apiService (response.data.error), poi resto della response
      if (error?.apiErrorMessage) {
        showAlertGlobal(String(error.apiErrorMessage).trim(), 'danger', 6000);
        return;
      }

      const data = error?.response?.data;
      let errorMessage = 'Errore nel salvataggio dell\'articolo';
      if (data != null) {
        if (typeof data === 'string' && data.trim()) {
          errorMessage = data.trim();
        } else if (data.error != null && String(data.error).trim() !== '') {
          errorMessage =
            typeof data.error === 'string'
              ? data.error
              : typeof data.error === 'object'
                ? JSON.stringify(data.error)
                : String(data.error);
        } else if (data.message != null && String(data.message).trim()) {
          errorMessage = String(data.message).trim();
        } else if (data.errors && typeof data.errors === 'object') {
          const firstKey = Object.keys(data.errors)[0];
          const firstVal = firstKey != null ? data.errors[firstKey] : null;
          if (Array.isArray(firstVal) && firstVal[0]) {
            errorMessage = String(firstVal[0]);
          } else if (typeof firstVal === 'string') {
            errorMessage = firstVal;
          }
        } else if (error.response?.statusText) {
          errorMessage = `${errorMessage}: ${error.response.statusText}`;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      // Toaster rosso (Bootstrap Alert variant "danger")
      showAlertGlobal(errorMessage, 'danger', 6000);
    } finally {
      setSaving(false);
    }
  };

  // Chiusura modal
  const handleClose = () => {
    resetForm();
    setErrors({});
    setImageFile(null);
    setUploadingImage(false);
    onHide();
  };

  // Gestione eliminazione categoria esistente
  const handleEliminaCategoria = (numeroGruppo, codCategoria) => {
    console.log('🗑️ === ELIMINA CATEGORIA ===');
    console.log(`🗑️ Elimina categoria gruppo ${numeroGruppo}:`, codCategoria);
    
    // Valida i parametri
    if (!numeroGruppo || numeroGruppo < 1 || numeroGruppo > 6) {
      console.error('❌ Numero gruppo non valido:', numeroGruppo);
      showAlertGlobal('Errore: numero gruppo non valido', 'danger');
      return;
    }
    
    if (!codCategoria || codCategoria.trim() === '') {
      console.error('❌ Codice categoria non valido:', codCategoria);
      showAlertGlobal('Errore: codice categoria non valido', 'danger');
      return;
    }
    
    // Determina quale array di dati usare e il nome del campo
    let gruppoData, fieldName, etichettaGruppo;
    
    switch(numeroGruppo) {
      case 1:
        gruppoData = gruppo1Data;
        fieldName = 'colore';
        etichettaGruppo = gruppo1Label;
        break;
      case 2:
        gruppoData = gruppo2Data;
        fieldName = 'tipo_arti';
        etichettaGruppo = gruppo2Label;
        break;
      case 3:
        gruppoData = gruppo3Data;
        fieldName = 'ngruppo3';
        etichettaGruppo = gruppo3Label;
        break;
      case 4:
        gruppoData = gruppo4Data;
        fieldName = 'ncolore';
        etichettaGruppo = gruppo4Label;
        break;
      case 5:
        gruppoData = gruppo5Data;
        fieldName = 'gruppo5';
        etichettaGruppo = gruppo5Label;
        break;
      case 6:
        gruppoData = gruppo6Data;
        fieldName = 'gruppo6';
        etichettaGruppo = gruppo6Label;
        break;
      default:
        console.error('❌ Numero gruppo non supportato:', numeroGruppo);
        showAlertGlobal('Errore: numero gruppo non supportato', 'danger');
        return;
    }
    
    // Trova il nome della categoria da eliminare
    const categoriaData = gruppoData.find(item => {
      const codice = (item.codice || item.VALOR || item || '').toString().trim();
      return codice === codCategoria;
    });
    
    const nomeCategoria = categoriaData 
      ? (categoriaData.nome || categoriaData.NOME || categoriaData.VALOR || categoriaData || '').toString().trim() 
      : codCategoria;
    
    // Imposta i dati per il modale di conferma ed aprilo
    setEliminaCategoriaData({
      numeroGruppo,
      codiceCategoria: codCategoria,
      nomeCategoria,
      etichettaGruppo,
      fieldName
    });
    
    setShowEliminaCategoriaModal(true);
  };

  // Funzione per confermare l'eliminazione dalla modale
  const handleConfermaEliminaCategoria = async () => {
    const { numeroGruppo, codiceCategoria, nomeCategoria, fieldName } = eliminaCategoriaData;
    
    console.log('🗑️ === CONFERMA ELIMINAZIONE CATEGORIA ===');
    console.log(`🗑️ Eliminazione confermata gruppo ${numeroGruppo}:`, {
      codice: codiceCategoria,
      nome: nomeCategoria
    });
    
    try {
      setEliminandoCategoria(true);
      
      // Chiamata API per eliminare la categoria
      await magService.deleteCategoria(numeroGruppo, codiceCategoria);
      showAlertGlobal(`Categoria "${nomeCategoria}" eliminata con successo!`, 'success');
      
      // Force refresh dei dati per aggiornare i combo
      await loadComboData(true);
      
      // Se la categoria eliminata era selezionata nel form, la rimuovi
      if (formData[fieldName] === codiceCategoria) {
        setFormData(prev => ({
          ...prev,
          [fieldName]: ''
        }));
      }
      
      // Chiudi il modale
      setShowEliminaCategoriaModal(false);
      
    } catch (error) {
      console.error('❌ Errore nell\'eliminazione della categoria:', error);
      showAlertGlobal(`Errore nell'eliminazione della categoria: ${error.message}`, 'danger');
    } finally {
      setEliminandoCategoria(false);
    }
  };

  // Funzione helper per ottenere il nome del campo dal gruppo
  const getFieldNameForGruppo = (gruppo) => {
    switch(gruppo) {
      case 'gruppo1': return 'colore';
      case 'gruppo2': return 'tipo_arti';
      case 'gruppo3': return 'ngruppo3';
      case 'gruppo4': return 'ncolore';
      case 'gruppo5': return 'gruppo5';
      case 'gruppo6': return 'gruppo6';
      default: return '';
    }
  };

  // Gestione creazione nuova categoria
  const handleNuovaCategoria = (gruppo) => {
    console.log('➕ === NUOVA CATEGORIA ===');
    console.log(`➕ Nuova categoria per ${gruppo}`);
    console.log('📋 gruppo1Label:', gruppo1Label);
    console.log('📋 gruppo2Label:', gruppo2Label);
    
    // Determina il numero del gruppo e l'etichetta
    let numeroGruppo;
    let etichettaGruppo;
    
    switch(gruppo) {
      case 'gruppo1':
        numeroGruppo = 1;
        etichettaGruppo = gruppo1Label;
        break;
      case 'gruppo2':
        numeroGruppo = 2;
        etichettaGruppo = gruppo2Label;
        break;
      case 'gruppo3':
        numeroGruppo = 3;
        etichettaGruppo = gruppo3Label;
        break;
      case 'gruppo4':
        numeroGruppo = 4;
        etichettaGruppo = gruppo4Label;
        break;
      case 'gruppo5':
        numeroGruppo = 5;
        etichettaGruppo = gruppo5Label;
        break;
      case 'gruppo6':
        numeroGruppo = 6;
        etichettaGruppo = gruppo6Label;
        break;
      default:
        console.error('Gruppo non riconosciuto:', gruppo);
        return;
    }
    
    // Imposta i dati del modale e aprilo
    setCategoriaModalData({
      numeroGruppo: numeroGruppo,
      etichettaGruppo: etichettaGruppo
    });
    setShowNuovaCategoriaModal(true);
  };


  
  // Funzione di salvataggio per il modale categoria
  const handleSaveNuovaCategoria = async (numeroGruppo, nomeCategoria) => {
    console.log('💾 === SALVATAGGIO NUOVA CATEGORIA ===');
    console.log('📝 Creazione nuova categoria:', {
      numeroGruppo: numeroGruppo,
      nome: nomeCategoria
    });
    
    try {
      // Chiama il servizio per inserire la nuova categoria
      const response = await magService.insertCategoria(numeroGruppo, nomeCategoria);
      console.log('✅ Categoria creata con successo:', response);
      
      showAlertGlobal(`Nuova categoria "${nomeCategoria}" creata con successo!`, 'success');
      
      // Piccolo delay per dare tempo al database di propagare
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Ricarica i dati delle categorie per aggiornare i combo CON FORCE REFRESH
      console.log('🔄 Forzando refresh cache categorie...');
      await loadComboData(true);
      
      // Debug: verifica i dati aggiornati
      console.log('🔍 Dati aggiornati gruppo', numeroGruppo, ':', {
        gruppo1Data: gruppo1Data.length,
        gruppo2Data: gruppo2Data.length,
        gruppo3Data: gruppo3Data.length,
        gruppo4Data: gruppo4Data.length,
        gruppo5Data: gruppo5Data.length,
        gruppo6Data: gruppo6Data.length
      });
      
      return response;
    } catch (error) {
      console.error('❌ Errore nella creazione della categoria:', error);
      showAlertGlobal(`Errore nella creazione della categoria: ${error.message}`, 'danger');
      throw error;
    }
  };

  const modalTitle = isNewArticolo ? 'Nuovo Articolo' : `Modifica Articolo: ${articoloDettagli?.cd_min || ''}`;

  return (
    <>
      <Modal 
        show={show} 
        onHide={handleClose}
        size={isMobile ? undefined : "xl"}
        fullscreen={isMobile}
        className={`${darkMode ? 'dark-modal' : ''} ${isMobile ? 'modal-fullscreen' : ''}`}
        backdrop="static"
      >
      <Modal.Header 
        closeButton 
        className={darkMode ? 'bg-dark text-light' : ''}
      >
        <Modal.Title>{modalTitle}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body 
        className={darkMode ? 'bg-dark text-light' : ''}
      >
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
            <p className="mt-3">Caricamento dati...</p>
          </div>
        ) : (
          <Tabs 
            defaultActiveKey="generale" 
            className={`mb-3 ${isMobile ? 'nav-tabs-mobile' : ''} ${hidePrezziMagazzinoTabs ? 'hide-tab-nav' : ''}`}
          >
            {/* Tab Generale */}
            <Tab eventKey="generale" title="Generale">
              <Form>
                <Row>
                  {/* Sezione dati principali - 8/12 */}
                  <Col md={8}>
                    <Row>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label>Codice Articolo *</Form.Label>
                          <Form.Control
                            type="text"
                            name="cd_min"
                            value={formData.cd_min}
                            onChange={handleInputChange}
                            isInvalid={!!errors.cd_min}
                            disabled={!isNewArticolo}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.cd_min}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col md={9}>
                        <Form.Group className="mb-3">
                          <Form.Label>Descrizione *</Form.Label>
                          <Form.Control
                            type="text"
                            name="descriz"
                            value={formData.descriz}
                            onChange={handleInputChange}
                            isInvalid={!!errors.descriz}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.descriz}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs={6} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Unità Misura *</Form.Label>
                          <Form.Select
                            name="unita_m"
                            value={formData.unita_m}
                            onChange={handleInputChange}
                            isInvalid={!!errors.unita_m}
                          >
                            <option value="">Seleziona unità...</option>
                            {unitaMisura
                              .filter((item) => {
                                const codice = (item.codice || item.VALOR || item || '').toString().trim();
                                return ['Kg', 'Nr', 'Pz'].some(u => u.toLowerCase() === codice.toLowerCase());
                              })
                              .map((item, index) => {
                                const codice = (item.codice || item.VALOR || item || '').toString().trim();
                                return (
                                  <option key={`unita-${index}`} value={codice}>
                                    {codice}
                                  </option>
                                );
                              })}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.unita_m}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col xs={6} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>IVA *</Form.Label>
                          <Form.Select
                            name="iva"
                            value={formData.iva}
                            onChange={handleInputChange}
                            isInvalid={!!errors.iva}
                          >
                            <option value="">Seleziona IVA...</option>
                            {aliquoteIva
                              .filter((item) => {
                                const codice = (item.codice || item.VALOR || item || '').toString().trim();
                                const num = parseFloat(codice);
                                return !isNaN(num) && num < 99;
                              })
                              .map((item, index) => {
                                const codice = (item.codice || item.VALOR || item || '').toString().trim();
                                return (
                                  <option key={`iva-${index}`} value={codice}>
                                    {codice}
                                  </option>
                                );
                              })}
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.iva}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    {/* Attiva articolo in promozione - sotto Unità di misura */}
                    <Row className="mt-2">
                      <Col xs={12}>
                        <div
                          className={`rounded p-3 ${formData.su_flash === '1' ? 'bg-warning bg-opacity-25 border border-warning' : darkMode ? 'bg-secondary bg-opacity-25 border border-secondary' : 'bg-light border'}`}
                          style={{ borderWidth: '2px' }}
                        >
                          <Form.Check
                            type="switch"
                            id="su_flash"
                            name="su_flash"
                            label={
                              <span
                                className={formData.su_flash === '1' ? 'fw-bold' : ''}
                                style={formData.su_flash === '1' && darkMode ? { color: '#fff' } : formData.su_flash === '1' ? { color: '#664d03' } : undefined}
                              >
                                <i className="fas fa-tag me-2"></i>
                                Attiva articolo in promozione
                              </span>
                            }
                            checked={formData.su_flash === '1'}
                            onChange={(e) => {
                              handleInputChange({
                                target: {
                                  name: 'su_flash',
                                  value: e.target.checked ? '1' : '0'
                                }
                              });
                            }}
                            className="fs-6"
                          />
                        </div>
                      </Col>
                    </Row>
                  </Col>

                  {/* Sezione immagine - 4/12 */}
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Immagine Prodotto</Form.Label>
                      <div className="text-center border rounded p-3" style={{ minHeight: '180px' }}>
                        {formData.cd_min ? (
                          <div>
                            <ProductImage
                              codice={formData.cd_min}
                              descriz={formData.descriz}
                              immagine={formData.urlimmagine}
                              style={{ maxHeight: '120px', maxWidth: '120px' }}
                            />
                            <div className="mt-3">
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handleImageFileChange}
                                disabled={uploadingImage}
                                className="d-none"
                                id="imageUpload"
                              />
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => document.getElementById('imageUpload').click()}
                                disabled={uploadingImage}
                              >
                                {uploadingImage ? (
                                  <>
                                    <Spinner size="sm" className="me-1" />
                                    Caricamento...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-upload me-1"></i>
                                    Cambia Immagine
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-muted">
                            <i className="fas fa-image fa-3x mb-2"></i>
                            <p>Inserisci prima il codice articolo</p>
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>


                {/* Separatore per le categorie */}
                <hr className="my-4" />
                <h6 className="mb-3 text-muted">
                  <i className="fas fa-tags me-2"></i>
                  Categorie e Classificazioni
                </h6>

                {/* Prima riga gruppi - Gruppo 1 e 2 (sempre presenti) */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{gruppo1Label}</Form.Label>
                      <div className="categoria-combo-container">
                        <div className="flex-grow-1">
                          <Form.Select
                            name="colore"
                            value={formData.colore}
                            onChange={handleInputChange}
                          >
                            <option value="">Seleziona {gruppo1Label.toLowerCase()}...</option>
                            {gruppo1Data.map((item, index) => {
                              const codice = (item.codice || item.VALOR || item || '').toString().trim();
                              const nome = (item.nome || item.NOME || item.VALOR || item || '').toString().trim();
                              return (
                                <option key={`gruppo1-${index}`} value={codice}>
                                  {nome}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </div>
                        <div className="categoria-buttons">
                          {formData.colore && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              title={`Elimina ${gruppo1Label}`}
                              onClick={() => handleEliminaCategoria(1, formData.colore)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          )}
                          <Button
                            variant="outline-success"
                            size="sm"
                            title={`Nuovo ${gruppo1Label}`}
                            onClick={() => handleNuovaCategoria('gruppo1')}
                          >
                            <i className="fas fa-plus"></i>
                          </Button>
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{gruppo2Label}</Form.Label>
                      <div className="categoria-combo-container">
                        <div className="flex-grow-1">
                          <Form.Select
                            name="tipo_arti"
                            value={formData.tipo_arti}
                            onChange={handleInputChange}
                          >
                            <option value="">Seleziona {gruppo2Label.toLowerCase()}...</option>
                            {gruppo2Data.map((item, index) => {
                              const codice = (item.codice || item.VALOR || item || '').toString().trim();
                              const nome = (item.nome || item.NOME || item.VALOR || item || '').toString().trim();
                              return (
                                <option key={`gruppo2-${index}`} value={codice}>
                                  {nome}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </div>
                        <div className="categoria-buttons">
                          {formData.tipo_arti && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              title={`Elimina ${gruppo2Label}`}
                              onClick={() => handleEliminaCategoria(2, formData.tipo_arti)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          )}
                          <Button
                            variant="outline-success"
                            size="sm"
                            title={`Nuovo ${gruppo2Label}`}
                            onClick={() => handleNuovaCategoria('gruppo2')}
                          >
                            <i className="fas fa-plus"></i>
                          </Button>
                        </div>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Seconda riga gruppi - Gruppo 3 e 4 (condizionali) */}
                <Row>
                  {gruppo3Data.length > 0 && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{gruppo3Label}</Form.Label>
                        <div className="categoria-combo-container">
                          <div className="flex-grow-1">
                            <Form.Select
                              name="ngruppo3"
                              value={formData.ngruppo3}
                              onChange={handleInputChange}
                            >
                              <option value="">Seleziona {gruppo3Label.toLowerCase()}...</option>
                              {gruppo3Data.map((item, index) => {
                                const codice = (item.codice || item.VALOR || item || '').toString().trim();
                                const nome = (item.nome || item.NOME || item.VALOR || item || '').toString().trim();
                                return (
                                  <option key={`gruppo3-${index}`} value={codice}>
                                    {nome}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>
                          <div className="categoria-buttons">
                            {formData.ngruppo3 && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                title={`Elimina ${gruppo3Label}`}
                                onClick={() => handleEliminaCategoria(3, formData.ngruppo3)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            )}
                            <Button
                              variant="outline-success"
                              size="sm"
                              title={`Nuovo ${gruppo3Label}`}
                              onClick={() => handleNuovaCategoria('gruppo3')}
                            >
                              <i className="fas fa-plus"></i>
                            </Button>
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                  )}
                  
                  {gruppo4Data.length > 0 && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{gruppo4Label}</Form.Label>
                        <div className="categoria-combo-container">
                          <div className="flex-grow-1">
                            <Form.Select
                              name="ncolore"
                              value={formData.ncolore}
                              onChange={handleInputChange}
                            >
                              <option value="">Seleziona {gruppo4Label.toLowerCase()}...</option>
                              {gruppo4Data.map((item, index) => {
                                const codice = (item.codice || item.VALOR || item || '').toString().trim();
                                const nome = (item.nome || item.NOME || item.VALOR || item || '').toString().trim();
                                return (
                                  <option key={`gruppo4-${index}`} value={codice}>
                                    {nome}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>
                          <div className="categoria-buttons">
                            {formData.ncolore && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                title={`Elimina ${gruppo4Label}`}
                                onClick={() => handleEliminaCategoria(4, formData.ncolore)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            )}
                            <Button
                              variant="outline-success"
                              size="sm"
                              title={`Nuovo ${gruppo4Label}`}
                              onClick={() => handleNuovaCategoria('gruppo4')}
                            >
                              <i className="fas fa-plus"></i>
                            </Button>
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                {/* Terza riga gruppi - Gruppo 5 e 6 (mantenuti ma invisibili) */}
                <Row className="d-none">
                  {gruppo5Data.length > 0 && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{gruppo5Label}</Form.Label>
                        <div className="categoria-combo-container">
                          <div className="flex-grow-1">
                            <Form.Select
                              name="gruppo5"
                              value={formData.gruppo5}
                              onChange={handleInputChange}
                            >
                              <option value="">Seleziona {gruppo5Label.toLowerCase()}...</option>
                              {gruppo5Data.map((item, index) => {
                                const codice = (item.codice || item.VALOR || item || '').toString().trim();
                                const nome = (item.nome || item.NOME || item.VALOR || item || '').toString().trim();
                                return (
                                  <option key={`gruppo5-${index}`} value={codice}>
                                    {nome}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>
                          <div className="categoria-buttons">
                            {formData.gruppo5 && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                title={`Elimina ${gruppo5Label}`}
                                onClick={() => handleEliminaCategoria(5, formData.gruppo5)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            )}
                            <Button
                              variant="outline-success"
                              size="sm"
                              title={`Nuovo ${gruppo5Label}`}
                              onClick={() => handleNuovaCategoria('gruppo5')}
                            >
                              <i className="fas fa-plus"></i>
                            </Button>
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                  )}
                  
                  {gruppo6Data.length > 0 && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>{gruppo6Label}</Form.Label>
                        <div className="categoria-combo-container">
                          <div className="flex-grow-1">
                            <Form.Select
                              name="gruppo6"
                              value={formData.gruppo6}
                              onChange={handleInputChange}
                            >
                              <option value="">Seleziona {gruppo6Label.toLowerCase()}...</option>
                              {gruppo6Data.map((item, index) => {
                                const codice = (item.codice || item.VALOR || item || '').toString().trim();
                                const nome = (item.nome || item.NOME || item.VALOR || item || '').toString().trim();
                                return (
                                  <option key={`gruppo6-${index}`} value={codice}>
                                    {nome}
                                  </option>
                                );
                              })}
                            </Form.Select>
                          </div>
                          <div className="categoria-buttons">
                            {formData.gruppo6 && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                title={`Elimina ${gruppo6Label}`}
                                onClick={() => handleEliminaCategoria(6, formData.gruppo6)}
                              >
                                <i className="fas fa-trash"></i>
                              </Button>
                            )}
                            <Button
                              variant="outline-success"
                              size="sm"
                              title={`Nuovo ${gruppo6Label}`}
                              onClick={() => handleNuovaCategoria('gruppo6')}
                            >
                              <i className="fas fa-plus"></i>
                            </Button>
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                  )}
                </Row>

                {/* Separatore per i dati tecnici */}
                <hr className="my-4" />
                <h6 className="mb-3 text-muted">
                  <i className="fas fa-weight me-2"></i>
                  Dati Tecnici e Pesi
                </h6>

                {/* Campi per pesi e misure */}
                <Row>
                  <Col xs={4} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pz x Teglia</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        name="pos"
                        value={formData.pos}
                        onChange={handleInputChange}
                        placeholder="Inserisci Pz x Teglia"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col xs={4} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>{(formData.unita_m || '').toLowerCase() === 'kg' ? 'Peso Kg per Pz' : 'Peso kg'}</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.001"
                        name="peso_lo"
                        value={formData.peso_lo}
                        onChange={handleInputChange}
                        placeholder="Inserisci peso in kg"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col xs={4} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Pz al Kg (calcolato)</Form.Label>
                      <Form.Control
                        type="text"
                        value={calcolaPzAlKg()}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                        placeholder="Calcolato automaticamente"
                      />
                      <Form.Text className="text-muted">
                        {formData.unita_m?.toLowerCase() === 'pz' || formData.unita_m?.toLowerCase() === 'pezzi' || formData.unita_m?.toLowerCase() === 'n'
                          ? 'Formula: 1 / Peso kg'
                          : formData.unita_m?.toLowerCase() === 'kg' 
                          ? 'Formula: 1 / (Peso Kg per Pz)'
                          : 'Calcolato come 1 / Peso kg'}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                
                {/* Campi PanifyCloud qta minima, scala*/}
                <Row>
                  <Col xs={6} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Q.tà minima</Form.Label>
                      <Form.Control
                        type="number"
                        name="qtaminima"
                        value={formData.qtaminima}
                        onChange={handleInputChange}
                        placeholder="Inserisci quantita minima"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col xs={6} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Scala Q.tà</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.001"
                        name="qtarange"
                        value={formData.qtarange}
                        onChange={handleInputChange}
                        placeholder="Inserisci il rage di quantita"
                      />
                    </Form.Group>
                  </Col>

                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Anticipo minimo per modificare l&apos;ordine (ore)</Form.Label>
                      <Form.Control
                        type="text"
                        inputMode="numeric"
                        name="orebloccoprod"
                        value={formData.orebloccoprod}
                        onChange={handleInputChange}
                        placeholder="0"
                        autoComplete="off"
                      />
                      <Form.Text className="text-muted">
                        Solo numeri interi; 0 = nessun anticipo obbligatorio oltre gli orari globali.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>



                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="su_web"
                        name="su_web"
                        label="Disponibile per distribuzione PanifyCloud"
                        checked={formData.su_web === '1'}
                        onChange={(e) => {
                          handleInputChange({
                            target: {
                              name: 'su_web',
                              value: e.target.checked ? '1' : '0'
                            }
                          });
                        }}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Note Estese</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="note_est"
                        value={formData.note_est}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Tab>

            {!hidePrezziMagazzinoTabs && (
            <>
            {/* Tab Prezzi */}
            <Tab eventKey="prezzi" title="Prezzi">
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Listino 1</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="listino1"
                        value={formData.listino1}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Listino 2</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="listino2"
                        value={formData.listino2}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Costo Medio</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="cost_medio"
                        value={formData.cost_medio}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Costo Unitario</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="costo_u"
                        value={formData.costo_u}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Costo Lordo</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="costo_lordo"
                        value={formData.costo_lordo}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sconto 1</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="sconto1"
                        value={formData.sconto1}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sconto 2</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="sconto2"
                        value={formData.sconto2}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Tab>

            {/* Tab Magazzino */}
            <Tab eventKey="magazzino" title="Magazzino">
              <Form>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Giacenza</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="giacenza"
                        value={formData.giacenza}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Impegnato</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="impegnato"
                        value={formData.impegnato}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Ordinato</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="ordinato"
                        value={formData.ordinato}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Scorta Minima</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="scort_m"
                        value={formData.scort_m}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Posizione</Form.Label>
                      <Form.Control
                        type="text"
                        name="posizione"
                        value={formData.posizione}
                        readOnly
                        className={darkMode ? 'bg-secondary text-light' : 'bg-light'}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Tab>
            </>
            )}
          </Tabs>
        )}
      </Modal.Body>
      
      <Modal.Footer 
        className={darkMode ? 'bg-dark border-secondary' : ''}
      >
        <div className={`d-flex gap-2 ${isMobile ? 'w-100' : ''}`}>
          <Button 
            variant="secondary" 
            onClick={handleClose} 
            disabled={saving}
            className={isMobile ? 'flex-fill' : ''}
          >
            Annulla
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave} 
            disabled={saving || loading}
            className={isMobile ? 'flex-fill' : ''}
          >
            {saving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                Salvataggio...
              </>
            ) : (
              isNewArticolo ? 'Crea Articolo' : 'Salva Modifiche'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
    
    {/* Modale per conferma eliminazione categoria */}
    <EliminaCategoriaModal
      show={showEliminaCategoriaModal}
      onHide={() => setShowEliminaCategoriaModal(false)}
      onConfirm={handleConfermaEliminaCategoria}
      categoriaData={eliminaCategoriaData}
      darkMode={darkMode}
      eliminando={eliminandoCategoria}
    />
    
    {/* Modale per nuova categoria */}
    <NuovaCategoriaModal
      show={showNuovaCategoriaModal}
      onHide={() => setShowNuovaCategoriaModal(false)}
      onSave={handleSaveNuovaCategoria}
      numeroGruppo={categoriaModalData.numeroGruppo}
      etichettaGruppo={categoriaModalData.etichettaGruppo}
      darkMode={darkMode}
    />
    </>
  );
};

export default EditArticolo;
